/**
 * @module Accordion
 */

/**
 * Состояние панели аккордеона.
 * @typedef {Object} AccordionState
 *
 * @property {jQuery} $btn Кнопка раскрытия панели.
 * @property {jQuery} $panel Панель аккордеона.
 * @property {Boolean} isExpanded Панель раскрыта.
 * @property {Boolean} isDisabled Панель заблокирована.
 */

import { media, toArray, getKeyName } from '../../../scripts/helpers';
import Component from '../Component';
import './styles.css';

/**
 * Аккордеон.
 *
 * @todo Реализовать кастомные эвенты открытия панелей.
 */
export default class Accordion extends Component {
    static className = 'accordion';

    static keyCodes = {
        END: 35,
        HOME: 36,
        UP: 38,
        DOWN: 40,
    };

    initElements() {
        this.elements = {
            $btns: $('.accordion__head-btn', this.$block),
            $panels: $('.accordion__panel', this.$block),
        };
    }

    setProperties({ isMultiple = true, isToggle = true, breakpoint, expanded = [], disabled = [] }) {
        /**
         * Разрешает множественное открытие панелей.
         * @type {Boolean}
         */
        this.isMultiple = isMultiple;

        /**
         * Разрешает сворачивание панели кликом по ней.
         * @type {Boolean}
         */
        this.isToggle = isToggle;

        if (breakpoint) {
            /**
             * Точка отключения аккордеона.
             * @type {MediaQueryList}
             */
            this.media = media(breakpoint);
        }

        /**
         * Индикатор отключенного аккордиона.
         * @type {Boolean}
         */
        this.isDestroy = this.media && !this.media.matches;

        /**
         * Начальные параметры открытых панелей.
         * @type {Number[]}
         */
        this.expanded = toArray(expanded);

        /**
         * Начальные параметры заблокированных панелей.
         * @type {Number[]}
         */
        this.disabled = toArray(disabled);

        /**
         * Массив состояний панелей аккордеона.
         * @type {AccordionState[]}
         */
        this.states = [];
    }

    init(options, pluginOptions) {
        super.init(options, pluginOptions);
        this.updateMarkup(options);
    }

    /**
     * Приводит разметку в соответствие параметрам.
     */
    updateMarkup() {
        this.initStates();

        if (this.media) {
            this.addResizeListener();
        }

        /* Убираем фокус с заголовков, т.к. аккордеон может быть отключен (при включении tabindex убирается). */
        this.elements.$btns.attr('tabindex', '-1');

        if (!this.isDestroy) {
            this.enable();
            /* Анимации нормально работают, только если расчёт высоты выполнен после загрузки страницы. */
            $(window).on('load', this.countPanelsHeight.bind(this));
        }
    }

    /**
     * Заполняет данные состояний панелей.
     */
    initStates() {
        this.elements.$btns.each((index, item) => {
            const $btn = $(item);
            const $panel = this.elements.$panels.eq(index);

            const isExpanded = this.expanded.includes(index);
            const isDisabled = this.disabled.includes(index);

            this.states.push({
                $btn,
                $panel,
                isExpanded,
                isDisabled,
            });
        });
    }

    /**
     * Прописывает обработчик перехода через брейкпоинт отключения аккордеона.
     */
    addResizeListener() {
        this.media.addListener(() => {
            this.isDestroy = !this.media.matches;
            if (this.isDestroy) {
                this.disable();
            } else {
                this.enable();
            }
        });
    }

    /**
     * Активирует аккордеон.
     */
    enable() {
        this.$block.addClass('accordion--init');

        this.addAdditionalMarkup();

        this.elements.$btns.on('click', handleBtnClick);
        this.elements.$btns.on('keydown', handleBtnKeydown);

        this.countPanelsHeight();
    }

    /**
     * Отключает аккордеон.
     */
    disable() {
        this.$block.removeClass('accordion--init');

        this.removeAdditionalMarkup();

        this.elements.$btns.off('click', handleBtnClick);
        this.elements.$btns.off('keydown', handleBtnKeydown);

        this.elements.$panels.css('height', '');
    }

    /**
     * Прописывает дополнительную разметку.
     */
    addAdditionalMarkup() {
        /* Добавляем роли */
        this.$block.attr('role', 'tablist');
        this.elements.$btns.attr('role', 'tab');
        this.elements.$panels.attr('role', 'tabpanel');

        /* Добавляем ARIA-состояния и классы */
        this.states.forEach(state => {
            if (state.isExpanded) {
                state.$btn.addClass('accordion__head-btn--expanded');
                state.$btn.attr('aria-expanded', 'true');

                if (this.isToggle) {
                    state.$btn.attr('aria-disabled', 'true');
                }
            } else {
                state.$btn.attr('aria-expanded', 'false');
                state.$panel.addClass('accordion__panel--hidden');
            }

            if (state.isDisabled) {
                state.$btn.addClass('accordion__head-btn--disabled');
                state.$btn.attr('aria-disabled', 'true');
            }
        });

        /* Добавляем ARIA-атрибуты */
        this.states.forEach(state => {
            state.$btn.attr('aria-controls', state.$panel.attr('id'));
            state.$panel.attr('aria-labelledby', state.$btn.attr('id'));
        });

        /* Возвращаем фокус на заголовки, если он был отключён. */
        this.elements.$btns.removeAttr('tabindex');
    }

    /**
     * Убирает дополнительную разметку.
     */
    removeAdditionalMarkup() {
        /* Очищаем блок. */
        this.$block.removeAttr('role');

        /* Очищаем кнопки. */
        this.elements.$btns.removeAttr('role aria-expanded aria-disabled aria-controls');
        this.elements.$btns.removeClass('accordion__head-btn--expanded accordion__head-btn--disabled');

        /* Очищаем панели. */
        this.elements.$panels.removeAttr('role aria-labelledby');
        this.elements.$panels.removeClass('accordion__panel--hidden');

        /* Убираем фокус с заголовков. */
        this.elements.$btns.attr('tabindex', '-1');
    }

    /**
     * Рассчитывает высоту панелей.
     */
    countPanelsHeight() {
        this.elements.$panels.each((index, item) => {
            const $panel = $(item);
            const $container = $('.accordion__panel-container', $panel);

            if ($panel.hasClass('accordion__panel--hidden')) {
                $panel.css('height', 0);
            } else {
                $panel.css('height', $container.outerHeight());
            }
        });
    }

    /**
     * Открывает панель.
     *
     * @param {Object} state Объект состояния панели.
     */
    openPanel(state) {
        const currentState = this.states.find(item => item === state);
        currentState.$btn.addClass('accordion__head-btn--expanded');
        currentState.$btn.attr('aria-expanded', true);
        currentState.$panel.removeClass('accordion__panel--hidden');
        currentState.isExpanded = true;

        const $container = $('.accordion__panel-container', currentState.$panel);
        /* Панели проставляется высота вложенного контейнера для работы анимации. Важно, что считывание проиходит после очистки класса accordion__panel--hidden, задающего display: none. */
        currentState.$panel.css('height', $container.outerHeight());

        if (!this.isToggle) {
            currentState.$btn.attr('aria-disabled', 'true');
        }
    }

    /**
     * Закрывает панель.
     *
     * @param {Object} state Объект состояния панели.
     */
    closePanel(state) {
        const currentState = this.states.find(item => item === state);
        currentState.$btn.removeClass('accordion__head-btn--expanded');
        currentState.$btn.attr('aria-expanded', false);
        currentState.isExpanded = false;

        /* У панели убирается высота для работы анимации. */
        currentState.$panel.css('height', 0);
        /* После того, как анимация отыграет (или чуть позже, некритично) ставится класс,
        добавляющий display: none, нужный для исключения панели из порядка табуляции. */
        setTimeout(() => {
            currentState.$panel.addClass('accordion__panel--hidden');
        }, 25);

        if (!this.isToggle) {
            currentState.$btn.removeAttr('aria-disabled');
        }
    }
}

function handleBtnClick(e) {
    const $btn = $(e.currentTarget);
    const $block = $btn.closest('.accordion');
    const accordion = Accordion.getInstance($block);

    if (accordion.isDestroy) return;

    const state = accordion.states.find(item => item.$btn.is($btn));

    if (state.isDisabled) return;

    /* Если разрешено открытие только одной панели и открывается ещё одна,
    то необходимо закрыть текущую активную, если такая имеется. */
    if (!accordion.isMultiple && !state.isExpanded) {
        const expandedState = accordion.states.filter(item => item.isExpanded);

        if (expandedState.length) {
            accordion.closePanel(expandedState[0]);
        }
    }

    if (!state.isExpanded) {
        accordion.openPanel(state);
    } else if (accordion.isToggle) {
        accordion.closePanel(state);
    }
}

function handleBtnKeydown(e) {
    const $btn = $(e.currentTarget);
    const { keyCode } = e;
    const key = getKeyName(Accordion.keyCodes, keyCode);

    if (!key) return;

    const $block = $btn.closest('.accordion');
    const accordion = Accordion.getInstance($block);

    if (accordion.isDestroy) return;

    const itemIndex = accordion.states.findIndex(item => item.$btn.is($btn));
    const panelsCount = accordion.states.length;

    let newIndex;

    if (['UP', 'DOWN'].includes(key)) {
        const direction = key === 'UP' ? -1 : 1;
        newIndex = (panelsCount + itemIndex + direction) % panelsCount;
    } else if (key === 'END') {
        newIndex = panelsCount - 1;
    } else if (key === 'HOME') {
        newIndex = 0;
    }

    accordion.states[newIndex].$btn[0].focus();

    e.preventDefault();
}
